import { ReactNode, useCallback, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';

/*
 * CopiedFlash — DESIGN_SYSTEM.md §2.6
 *
 * Copy-button wrapper that flashes success-tone for 1.2s after a copy.
 *
 * Two forms:
 *   1. Stand-alone <CopyButton text="…" /> — renders a small copy icon
 *      button that handles the navigator.clipboard.writeText() itself.
 *   2. Headless `useCopyFlash()` hook returning `{ copied, copy }` for
 *      callers that already own the trigger element.
 */

interface CopyButtonProps {
  /** Text to write to clipboard. */
  text: string;
  /** Label rendered next to the icon. Pass `null` to render icon only. */
  label?: ReactNode;
  copiedLabel?: ReactNode;
  className?: string;
  /** Optional success callback. */
  onCopied?: () => void;
}

export function CopyButton({
  text,
  label = 'Copy',
  copiedLabel = 'Copied',
  className,
  onCopied,
}: CopyButtonProps) {
  const { copied, copy } = useCopyFlash();
  const handleClick = () => {
    copy(text);
    onCopied?.();
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-body-xs font-medium transition-colors',
        copied
          ? 'bg-success-soft text-success-text'
          : 'bg-surface-2 text-soft hover:text-ink',
        className,
      )}
      aria-live="polite"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? copiedLabel : label}
    </button>
  );
}

/**
 * Headless variant. Returns the `copied` flag (true for ~1.2s after the
 * last copy) and a `copy()` callback that writes to the clipboard.
 */
export function useCopyFlash(durationMs = 1200) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        window.setTimeout(() => setCopied(false), durationMs);
      } catch {
        // Clipboard access may be denied (insecure context, permission). Swallow.
      }
    },
    [durationMs],
  );

  return { copied, copy };
}
